import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Loader2, Home, Calendar } from "lucide-react";

export function DipendenteFormDialog({
  open, onClose, editingId, form, setForm, onSubmit, saving,
  appartamenti, centriCosto,
  checkAssegnazioneChanged, showDataVariazione, dataVariazione, setDataVariazione,
  attivoIniziale = true,
  t
}) {
  const staDisattivando = editingId && attivoIniziale && !form.attivo;
  const dataUscitaMancante = staDisattivando && !form.data_uscita;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingId ? t("employees.editEmployee") : t("employees.newEmployee")}</DialogTitle>
          <DialogDescription>
            {editingId ? t("employees.editDescription") : t("employees.newDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("auth.lastName")} *</Label>
              <Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} placeholder="Rossi" />
            </div>
            <div>
              <Label>{t('common.name')} *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Mario" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="attivo" checked={form.attivo} onCheckedChange={(checked) => setForm({ ...form, attivo: checked })} />
            <Label htmlFor="attivo" className="cursor-pointer">{t("dialogs.activeEmployee")}</Label>
          </div>
          {staDisattivando && (
            <div className={`p-3 rounded-lg border ${dataUscitaMancante ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-200'}`}>
              <Label className={`flex items-center gap-2 ${dataUscitaMancante ? 'text-red-700' : 'text-amber-800'}`} htmlFor="data-uscita-required">
                <Calendar className="h-4 w-4" /> {t("dialogs.exitDateRequired")} * <span className="text-xs font-normal">{t("dialogs.exitDateMandatory")}</span>
              </Label>
              <DateInput
                id="data-uscita-required"
                value={form.data_uscita}
                onChange={(e) => setForm({ ...form, data_uscita: e.target.value })}
                className={`mt-2 ${dataUscitaMancante ? 'border-red-500 ring-1 ring-red-300' : ''}`}
                data-testid="input-data-uscita-required"
              />
              <p className={`text-xs mt-1 ${dataUscitaMancante ? 'text-red-700' : 'text-amber-700'}`}>
                {dataUscitaMancante
                  ? t("dialogs.exitDateRequiredWarning")
                  : t("dialogs.exitDateInfo")}
              </p>
            </div>
          )}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3 flex items-center gap-2"><Home className="h-4 w-4" /> {t("dialogs.assignment")}</h4>
            <div className="space-y-4">
              <div>
                <Label>{t("apartments.title").replace(/i$/, "o")}</Label>
                <SearchableSelect
                  options={[{ value: "__none__", label: t("dialogs.noneOption") }, ...appartamenti.map(a => ({ value: a.id, label: `${a.nome || a.indirizzo} - ${a.citta}` }))]}
                  value={form.appartamento_id || ""}
                  onValueChange={(v) => { setForm({ ...form, appartamento_id: v }); if (checkAssegnazioneChanged) checkAssegnazioneChanged('appartamento_id', v); }}
                  placeholder={t("dialogs.selectApartment")} searchPlaceholder={t("dialogs.searchApartment")} testId="select-appartamento"
                />
              </div>
              <div>
                <Label>{t("dialogs.costCenterSite")}</Label>
                <SearchableSelect
                  options={[{ value: "__none__", label: t("dialogs.noneOption") }, ...centriCosto.map(cc => ({ value: cc.id, label: `${cc.codice} - ${cc.nome}` }))]}
                  value={form.centro_costo_id || ""}
                  onValueChange={(v) => { setForm({ ...form, centro_costo_id: v }); if (checkAssegnazioneChanged) checkAssegnazioneChanged('centro_costo_id', v); }}
                  placeholder={t("dialogs.selectSite")} searchPlaceholder={t("dialogs.searchCostCenter")} testId="select-centro-costo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("dialogs.entryDate")}</Label>
                  <DateInput value={form.data_ingresso} onChange={(e) => setForm({ ...form, data_ingresso: e.target.value })} />
                </div>
                <div>
                  <Label>{t("dialogs.exitDate")}</Label>
                  <DateInput
                    value={form.data_uscita}
                    onChange={(e) => setForm({ ...form, data_uscita: e.target.value })}
                    disabled={staDisattivando}
                    title={staDisattivando ? t("dialogs.fillExitDateAbove") : ""}
                  />
                </div>
              </div>
              {!editingId && (
                <div>
                  <Label>{t("dialogs.assignmentDate")}</Label>
                  <DateInput value={form.data_assegnazione} onChange={(e) => setForm({ ...form, data_assegnazione: e.target.value })} />
                </div>
              )}
              {showDataVariazione && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Label className="text-yellow-800 flex items-center gap-2"><Calendar className="h-4 w-4" /> {t("dialogs.variationDate")} *</Label>
                  <DateInput value={dataVariazione} onChange={(e) => setDataVariazione(e.target.value)} className="mt-2" />
                  <p className="text-xs text-yellow-700 mt-1">{t("dialogs.variationRequired")}</p>
                </div>
              )}
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <h4 className="font-medium flex items-center gap-2">{t("dialogs.documentsResidence")}</h4>
            <div className="flex items-center gap-3">
              <Checkbox
                id="patente"
                checked={!!form.patente}
                onCheckedChange={(checked) => setForm({ ...form, patente: !!checked })}
                data-testid="checkbox-patente"
              />
              <Label htmlFor="patente" className="cursor-pointer">{t("dialogs.drivingLicense")}</Label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="has-residenza"
                  checked={!!form.has_residenza}
                  onCheckedChange={(checked) => setForm({
                    ...form,
                    has_residenza: !!checked,
                    residenza_appartamento_id: !checked ? "" : form.residenza_appartamento_id
                  })}
                  data-testid="checkbox-residenza"
                />
                <Label htmlFor="has-residenza" className="cursor-pointer">
                  {t("dialogs.companyResidence")}
                </Label>
              </div>
              {form.has_residenza && (
                <div className="pl-7">
                  <Label className="text-xs">{t("dialogs.residenceApartment")}</Label>
                  <SearchableSelect
                    options={appartamenti.map(a => ({ value: a.id, label: `${a.nome || a.indirizzo} - ${a.citta}` }))}
                    value={form.residenza_appartamento_id || ""}
                    onValueChange={(v) => setForm({ ...form, residenza_appartamento_id: v })}
                    placeholder={t("dialogs.selectResidenceApartment")}
                    searchPlaceholder={t("dialogs.searchApartment")}
                    testId="select-residenza"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {t("dialogs.residenceCanDiffer")}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="border-t pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("dialogs.fiscalCode")}</Label>
                <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} placeholder="RSSMRA..." />
              </div>
              <div>
                <Label>{t("dialogs.nationality")}</Label>
                <Input value={form.nazionalita} onChange={(e) => setForm({ ...form, nazionalita: e.target.value })} placeholder="Italiana" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("dialogs.qualification")}</Label>
                <Input value={form.qualifica} onChange={(e) => setForm({ ...form, qualifica: e.target.value })} placeholder="Operaio" />
              </div>
              <div>
                <Label>{t("dialogs.phone")}</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39..." />
              </div>
            </div>
          </div>
          <div>
            <Label>{t("common.notes")}</Label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("common.notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.create"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditMovimentoDialog({
  open, onClose, editMovimento, editMovForm, setEditMovForm,
  onSave, appartamenti, centriCosto, t
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("movements.editMovement")}</DialogTitle>
          <DialogDescription>
            {editMovimento?.dipendente_cognome} {editMovimento?.dipendente_nome}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>{t("apartments.title").replace(/i$/, "o")}</Label>
            <select className="w-full p-2 border rounded-md mt-1" value={editMovForm.appartamento_id} onChange={e => setEditMovForm({...editMovForm, appartamento_id: e.target.value})}>
              <option value="">{t("dialogs.noneOption")}</option>
              {appartamenti.map(a => (
                <option key={a.id} value={a.id}>{a.indirizzo} - {a.citta}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t("costCenters.title")}</Label>
            <select className="w-full p-2 border rounded-md mt-1" value={editMovForm.centro_costo_id} onChange={e => setEditMovForm({...editMovForm, centro_costo_id: e.target.value})}>
              <option value="">{t("dialogs.noneOption")}</option>
              {centriCosto.map(cc => (
                <option key={cc.id} value={cc.id}>{cc.codice} - {cc.nome}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("apartments.startDate")}</Label>
              <DateInput value={editMovForm.data_inizio} onChange={e => setEditMovForm({...editMovForm, data_inizio: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>{t("apartments.endDate")}</Label>
              <DateInput value={editMovForm.data_fine} onChange={e => setEditMovForm({...editMovForm, data_fine: e.target.value})} className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={onSave}>{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
